<script setup lang="ts">
import { del, get, post, put } from '@/api'

definePage({
  meta: {
    action: 'manage',
    subject: 'Auth',
  },
})

const { snackbar } = useErrorHandler()

// API 키 인터페이스
interface McpApiKey {
  id: number
  name: string
  scopes: string[]
  last_used_at: string | null
  expires_at: string | null
  is_active: boolean
  created_at: string
}

// 상태
const apiKeys = ref<McpApiKey[]>([])
const loading = ref(false)
const saving = ref(false)
const deleteLoading = ref<number | null>(null)
const regenerateLoading = ref<number | null>(null)

// 다이얼로그
const createDialog = ref(false)
const newKeyDialog = ref(false)
const newKey = ref('')

// 폼 데이터
const formData = ref({
  name: '',
  expires_at: '',
  scopes: ['*'] as string[],
})

// 테이블 헤더
const headers = [
  { title: '이름', key: 'name', width: '200px' },
  { title: '스코프', key: 'scopes', width: '150px' },
  { title: '마지막 사용', key: 'last_used_at', width: '150px' },
  { title: '만료일', key: 'expires_at', width: '150px' },
  { title: '상태', key: 'is_active', width: '100px' },
  { title: '생성일', key: 'created_at', width: '150px' },
  { title: '액션', key: 'actions', width: '180px', sortable: false },
]

// 날짜 포맷
const formatDate = (date: string | null): string => {
  if (!date)
    return '-'

  return new Date(date).toLocaleDateString('ko-KR', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  })
}

// 만료 여부 확인
const isExpired = (date: string | null): boolean => {
  if (!date)
    return false

  return new Date(date) < new Date()
}

// API 키 목록 로드
const loadApiKeys = async () => {
  loading.value = true
  try {
    const { success, data } = await get<{ data: McpApiKey[] }>('/mcp-api-keys')
    if (success && data)
      apiKeys.value = data.data
  }
  catch (error) {
    console.error('Failed to load API keys:', error)
    showNotification('API 키 목록을 불러오는데 실패했습니다.', 'error')
  }
  finally {
    loading.value = false
  }
}

// 새 API 키 생성
const createApiKey = async () => {
  if (!formData.value.name.trim()) {
    showNotification('API 키 이름을 입력해주세요.', 'warning')

    return
  }

  saving.value = true
  try {
    const payload: Record<string, unknown> = {
      name: formData.value.name,
      scopes: formData.value.scopes,
    }

    if (formData.value.expires_at)
      payload.expires_at = new Date(formData.value.expires_at).toISOString()

    const { success, data } = await post<{ message: string; key: string; data: McpApiKey }>('/mcp-api-keys', payload)

    if (success && data) {
      createDialog.value = false
      newKey.value = data.key
      newKeyDialog.value = true
      resetForm()
      await loadApiKeys()
      showNotification('API 키가 생성되었습니다.')
    }
    else {
      showNotification('API 키 생성에 실패했습니다.', 'error')
    }
  }
  catch (error) {
    console.error('Failed to create API key:', error)
    showNotification('API 키 생성에 실패했습니다.', 'error')
  }
  finally {
    saving.value = false
  }
}

// API 키 활성화/비활성화 토글
const toggleActive = async (key: McpApiKey) => {
  try {
    const { success } = await put(`/mcp-api-keys/${key.id}`, { is_active: !key.is_active })

    if (success) {
      key.is_active = !key.is_active
      showNotification(key.is_active ? 'API 키가 활성화되었습니다.' : 'API 키가 비활성화되었습니다.')
    }
    else {
      showNotification('상태 변경에 실패했습니다.', 'error')
    }
  }
  catch (error) {
    console.error('Failed to toggle API key:', error)
    showNotification('상태 변경에 실패했습니다.', 'error')
  }
}

// API 키 삭제
const deleteApiKey = async (key: McpApiKey) => {
  if (!confirm(`"${key.name}" API 키를 삭제하시겠습니까?\n이 작업은 되돌릴 수 없습니다.`))
    return

  deleteLoading.value = key.id
  try {
    const { success } = await del(`/mcp-api-keys/${key.id}`)

    if (success) {
      await loadApiKeys()
      showNotification('API 키가 삭제되었습니다.')
    }
    else {
      showNotification('삭제에 실패했습니다.', 'error')
    }
  }
  catch (error) {
    console.error('Failed to delete API key:', error)
    showNotification('삭제에 실패했습니다.', 'error')
  }
  finally {
    deleteLoading.value = null
  }
}

// API 키 재발급
const regenerateApiKey = async (key: McpApiKey) => {
  if (!confirm(`"${key.name}" API 키를 재발급하시겠습니까?\n기존 키는 즉시 무효화됩니다.`))
    return

  regenerateLoading.value = key.id
  try {
    const { success, data } = await post<{ message: string; key: string }>(`/mcp-api-keys/${key.id}/regenerate`)

    if (success && data) {
      newKey.value = data.key
      newKeyDialog.value = true
      showNotification('API 키가 재발급되었습니다.')
    }
    else {
      showNotification('재발급에 실패했습니다.', 'error')
    }
  }
  catch (error) {
    console.error('Failed to regenerate API key:', error)
    showNotification('재발급에 실패했습니다.', 'error')
  }
  finally {
    regenerateLoading.value = null
  }
}

// 새 키 복사
const copyNewKey = async () => {
  try {
    await navigator.clipboard.writeText(newKey.value)
    showNotification('API 키가 클립보드에 복사되었습니다.')
  }
  catch {
    // 폴백
    const input = document.createElement('input')

    input.value = newKey.value
    document.body.appendChild(input)
    input.select()
    document.execCommand('copy')
    document.body.removeChild(input)
    showNotification('API 키가 클립보드에 복사되었습니다.')
  }
}

// 폼 리셋
const resetForm = () => {
  formData.value = {
    name: '',
    expires_at: '',
    scopes: ['*'],
  }
}

// 알림 표시
const showNotification = (message: string, color: 'success' | 'error' | 'warning' | 'info' = 'success') => {
  snackbar.show(message, color)
}

// 초기 로드
onMounted(() => {
  loadApiKeys()
})
</script>

<template>
  <VRow>
    <VCol cols="12">
      <VCard>
        <VCardTitle class="d-flex align-center justify-space-between">
          <span>MCP API 키 관리</span>
          <div class="d-flex gap-2">
            <VBtn
              icon="tabler-refresh"
              variant="text"
              :loading="loading"
              @click="loadApiKeys"
            />
            <VBtn
              color="primary"
              prepend-icon="tabler-plus"
              @click="createDialog = true"
            >
              새 API 키
            </VBtn>
          </div>
        </VCardTitle>

        <VCardText>
          <VAlert
            type="info"
            variant="tonal"
            class="mb-4"
          >
            MCP 서버 연동에 사용되는 API 키를 관리합니다. 생성된 키는 한 번만 표시되므로 안전하게 보관하세요.
          </VAlert>

          <VDataTable
            :headers="headers"
            :items="apiKeys"
            :loading="loading"
            density="compact"
            :items-per-page="10"
          >
            <template #item.scopes="{ item }">
              <VChip
                v-for="scope in item.scopes"
                :key="scope"
                size="small"
                class="me-1"
              >
                {{ scope }}
              </VChip>
            </template>

            <template #item.last_used_at="{ item }">
              {{ formatDate(item.last_used_at) }}
            </template>

            <template #item.expires_at="{ item }">
              <span :class="{ 'text-error': isExpired(item.expires_at) }">
                {{ formatDate(item.expires_at) }}
                <VIcon
                  v-if="isExpired(item.expires_at)"
                  icon="tabler-alert-circle"
                  size="16"
                  color="error"
                  class="ms-1"
                />
              </span>
            </template>

            <template #item.is_active="{ item }">
              <VSwitch
                :model-value="item.is_active"
                :disabled="isExpired(item.expires_at)"
                hide-details
                density="compact"
                color="success"
                @update:model-value="toggleActive(item)"
              />
            </template>

            <template #item.created_at="{ item }">
              {{ formatDate(item.created_at) }}
            </template>

            <template #item.actions="{ item }">
              <div class="d-flex gap-1">
                <VBtn
                  icon="tabler-refresh"
                  size="small"
                  variant="text"
                  color="warning"
                  :loading="regenerateLoading === item.id"
                  title="재발급"
                  @click="regenerateApiKey(item)"
                />
                <VBtn
                  icon="tabler-trash"
                  size="small"
                  variant="text"
                  color="error"
                  :loading="deleteLoading === item.id"
                  title="삭제"
                  @click="deleteApiKey(item)"
                />
              </div>
            </template>

            <template #no-data>
              <div class="text-center py-4">
                <VIcon
                  icon="tabler-key-off"
                  size="48"
                  color="grey"
                  class="mb-2"
                />
                <p class="text-grey">
                  등록된 API 키가 없습니다.
                </p>
              </div>
            </template>
          </VDataTable>
        </VCardText>
      </VCard>
    </VCol>

    <!-- 새 API 키 생성 다이얼로그 -->
    <VDialog
      v-model="createDialog"
      max-width="500"
      persistent
    >
      <VCard>
        <VCardTitle>새 API 키 생성</VCardTitle>
        <VCardText>
          <VTextField
            v-model="formData.name"
            label="API 키 이름"
            placeholder="예: Production MCP Server"
            required
            class="mb-4"
          />

          <VTextField
            v-model="formData.expires_at"
            label="만료일 (선택)"
            type="datetime-local"
            hint="설정하지 않으면 만료되지 않습니다"
            persistent-hint
            class="mb-4"
          />

          <VSelect
            v-model="formData.scopes"
            label="스코프"
            :items="[
              { title: '전체 권한', value: '*' },
              { title: '읽기 전용', value: 'read' },
              { title: '리뷰 관리', value: 'review:*' },
            ]"
            multiple
            chips
            closable-chips
          />
        </VCardText>
        <VCardActions>
          <VSpacer />
          <VBtn
            variant="text"
            @click="createDialog = false; resetForm()"
          >
            취소
          </VBtn>
          <VBtn
            color="primary"
            :loading="saving"
            @click="createApiKey"
          >
            생성
          </VBtn>
        </VCardActions>
      </VCard>
    </VDialog>

    <!-- 새 키 표시 다이얼로그 -->
    <VDialog
      v-model="newKeyDialog"
      max-width="600"
      persistent
    >
      <VCard>
        <VCardTitle class="d-flex align-center">
          <VIcon
            icon="tabler-key"
            color="success"
            class="me-2"
          />
          API 키가 생성되었습니다
        </VCardTitle>
        <VCardText>
          <VAlert
            type="warning"
            variant="tonal"
            class="mb-4"
          >
            이 키는 다시 표시되지 않습니다. 반드시 안전한 곳에 복사해서 보관하세요.
          </VAlert>

          <VTextField
            :model-value="newKey"
            readonly
            variant="outlined"
            class="font-weight-bold"
          >
            <template #append>
              <VBtn
                icon="tabler-copy"
                variant="text"
                @click="copyNewKey"
              />
            </template>
          </VTextField>

          <div class="mt-4">
            <p class="text-subtitle-2 mb-2">
              Claude Desktop 설정 예시:
            </p>
            <pre class="bg-surface-variant pa-3 rounded text-caption">{{ `{
  "mcpServers": {
    "regle": {
      "url": "https://mcp-server.regle.co.kr/mcp",
      "headers": {
        "Authorization": "Bearer ${newKey}"
      }
    }
  }
}` }}</pre>
          </div>
        </VCardText>
        <VCardActions>
          <VSpacer />
          <VBtn
            color="primary"
            @click="newKeyDialog = false; newKey = ''"
          >
            확인
          </VBtn>
        </VCardActions>
      </VCard>
    </VDialog>

    <!-- 스낵바 -->
    <VSnackbar
      v-model="snackbar.isVisible.value"
      :color="snackbar.color.value"
      :timeout="snackbar.timeout.value"
    >
      {{ snackbar.message.value }}
    </VSnackbar>
  </VRow>
</template>

<style scoped>
pre {
  white-space: pre-wrap;
  word-break: break-all;
}
</style>
